/**
 * useTextColorOverrides Hook
 * 
 * Manages custom text color overrides for title and body text
 */

import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem, removeStorageItem } from '../services/storage.service';

export interface UseTextColorOverridesReturn {
  titleColorOverride: string | null;
  bodyColorOverride: string | null;
  setTitleColor: (color: string) => void;
  setBodyTextColor: (color: string) => void;
  clearTextColorOverrides: () => void;
}

export const useTextColorOverrides = (): UseTextColorOverridesReturn => {
  const [titleColorOverride, setTitleColorOverride] = useState<string | null>(null);
  const [bodyColorOverride, setBodyColorOverride] = useState<string | null>(null);

  // Load overrides from localStorage on mount
  useEffect(() => {
    const savedTitleOverride = getStorageItem<string>('title-color-override');
    const savedBodyOverride = getStorageItem<string>('body-color-override');
    
    if (savedTitleOverride) {
      setTitleColorOverride(savedTitleOverride);
    }
    if (savedBodyOverride) {
      setBodyColorOverride(savedBodyOverride);
    }
  }, []);

  const setTitleColor = useCallback((color: string) => {
    setTitleColorOverride(color);
    setStorageItem('title-color-override', color);
  }, []);

  const setBodyTextColor = useCallback((color: string) => {
    setBodyColorOverride(color);
    setStorageItem('body-color-override', color);
  }, []);

  const clearTextColorOverrides = useCallback(() => {
    setTitleColorOverride(null);
    setBodyColorOverride(null);
    removeStorageItem('title-color-override');
    removeStorageItem('body-color-override');
    console.log('[TextColor] Text color overrides cleared');
  }, []);

  return {
    titleColorOverride,
    bodyColorOverride,
    setTitleColor,
    setBodyTextColor,
    clearTextColorOverrides
  };
};

